import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { conversionsKeys } from "@/features/dashboard/queries/conversions.queries";
import { api } from "@/lib/axios";
import { cn } from "@/lib/utils";
import { useReviewStore } from "@/stores/review.store";
import { Batch, ExtractedData } from "@/types/dashboard.types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, CircleCheck, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PdfViewer } from "./pdf-viewer";
import { Alert } from "./alert";
import ErrorAlert from "./error-alert";

export function ReviewInterface({ batch }: { batch: Batch }) {
  const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);
  const queryClient = useQueryClient();
  const {
    editedBatch: storedBatch,
    setEditedBatch: setStoredBatch,
    markAsReviewed,
  } = useReviewStore();
  const { mutateAsync: calculateGpa, isPending: isCalculating } = useMutation({
    mutationFn: (batch: Batch) =>
      api.post(`/api/conversion/calculate-gpa`, batch),
    onSettled: () => {
      toast.success("GPA Calculation is in progress");
      queryClient.invalidateQueries({
        queryKey: conversionsKeys.detail(batch.id),
      });
    },
  });

  const [editedBatch, setEditedBatch] = useState<Batch>(() => {
    if (storedBatch && storedBatch.id === batch.id) {
      return storedBatch;
    }
    return batch;
  });
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    const unloadCallback = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
      return "";
    };

    if (isDirty) {
      window.addEventListener("beforeunload", unloadCallback);
    }
    return () => window.removeEventListener("beforeunload", unloadCallback);
  }, [isDirty]);

  useEffect(() => {
    if (storedBatch && storedBatch.id === batch.id) {
      setEditedBatch(storedBatch);
    } else {
      setEditedBatch(batch);
    }
  }, [batch, storedBatch]);

  useEffect(() => {
    setIsDirty(false);
  }, [currentDocumentIndex]);

  const isLastDocument =
    currentDocumentIndex === editedBatch.documents.length - 1;
  const isFirstDocument = currentDocumentIndex === 0;
  const currentDocument = editedBatch.documents[currentDocumentIndex];

  // Calculate progress value
  const progress =
    editedBatch.documents.length === 1
      ? 100
      : (currentDocumentIndex / (editedBatch.documents.length - 1)) * 100;

  const handleNext = () => {
    if (isDirty) {
      toast.warning("You have unsaved changes");
      return;
    }
    if (!isLastDocument) {
      setCurrentDocumentIndex((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (isDirty) {
      toast.warning("You have unsaved changes");
      return;
    }
    if (!isFirstDocument) {
      setCurrentDocumentIndex((prev) => prev - 1);
    }
  };

  const handleStudentInfoChange = (field: "name" | "grade", value: string) => {
    const newDocuments = [...editedBatch.documents];
    const currentDoc = newDocuments[currentDocumentIndex];

    // Ensure extractedData and studentInformation exist
    const extractedData = currentDoc.extractedData || {
      table: [],
      studentInformation: { name: "", grade: "" },
    };

    newDocuments[currentDocumentIndex] = {
      ...currentDoc,
      extractedData: {
        ...extractedData,
        studentInformation: {
          ...extractedData.studentInformation,
          [field]: value,
        },
      },
    };

    setEditedBatch({
      ...editedBatch,
      documents: newDocuments,
    });
    setIsDirty(true);
  };

  const handleTableChange = (newTableData: ExtractedData["table"]) => {
    const newDocuments = [...editedBatch.documents];
    const currentDoc = newDocuments[currentDocumentIndex];

    // Ensure extractedData exists
    const extractedData = currentDoc.extractedData || {
      table: [],
      studentInformation: { name: "", grade: "" },
    };

    newDocuments[currentDocumentIndex] = {
      ...currentDoc,
      extractedData: {
        ...extractedData,
        table: newTableData,
      },
    };

    setEditedBatch({
      ...editedBatch,
      documents: newDocuments,
    });
    setIsDirty(true);
  };

  const handleSave = () => {
    setStoredBatch(editedBatch);
    markAsReviewed(currentDocument.id);
    setIsDirty(false);
    toast.success("Changes saved for the current document.");
  };

  const handleConvert = async (storedBatch: Batch) => {
    if (isDirty) {
      toast.warning("You have unsaved changes");
      return;
    }

    // if (isConvertDisabled) {
    //   toast.warning("Please fill in the missing information");
    //   return;
    // }

    await calculateGpa(storedBatch);
  };

  const currentDocumentHasMissingFields =
    !currentDocument.extractedData ||
    currentDocument.extractedData?.studentInformation?.name === "" ||
    currentDocument.extractedData?.studentInformation?.grade === "" ||
    currentDocument.extractedData?.table.length === 0 ||
    currentDocument.extractedData?.table.some(
      (row) => row.subject === "" || row.credits === "" || row.score === ""
    );

  const hasTableData = Boolean(currentDocument.extractedData?.table);

  const hasErrorMessage = Boolean(currentDocument.errorMessage);

  const allDocumentsComplete = editedBatch.documents.every((doc) => {
    if (!doc.extractedData) return false;
    if (doc.extractedData.studentInformation?.name === "") return false;
    if (doc.extractedData.studentInformation?.grade === "") return false;
    if (doc.extractedData.table.length === 0) return false;

    return !doc.extractedData.table.some(
      (row) => row.subject === "" || row.credits === "" || row.score === ""
    );
  });

  // const isConvertDisabled =
  //   editedBatch.documents.some(
  //     (doc) =>
  //       doc.extractedData?.studentInformation?.name === "" ||
  //       doc.extractedData?.studentInformation?.grade === "" ||
  //       doc.extractedData?.table.length === 0
  //   );

  return (
    <div className="mx-auto max-w-7xl px-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-base font-bold text-gray-900">
            Review Transcript
          </h1>
          <p className="text-gray-600 text-xs">
            Verify the extracted information for each document
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        <div className="rounded-xl shadow-sm border space-y-5 p-4">
          {hasTableData && currentDocumentHasMissingFields ? <Alert /> : null}
          {hasErrorMessage ? (
            <ErrorAlert
              message={currentDocument.errorMessage ?? ""}
              countryId={batch.countryId}
              gradingScaleId={batch.scaleId}
              key={currentDocument.id}
            />
          ) : null}
          {hasTableData && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <p className="text-sm shrink-0">Student Name: </p>
                <Input
                  value={
                    currentDocument.extractedData?.studentInformation?.name ||
                    ""
                  }
                  placeholder="Name of the student"
                  disabled={isCalculating}
                  onChange={(e) => {
                    handleStudentInfoChange("name", e.target.value);
                  }}
                />
              </div>
              <div className="flex items-center gap-2">
                <p className="text-sm">Grade: </p>
                <Input
                  value={
                    currentDocument.extractedData?.studentInformation?.grade ||
                    ""
                  }
                  placeholder="Grade of the student"
                  disabled={isCalculating}
                  onChange={(e) => {
                    handleStudentInfoChange("grade", e.target.value);
                  }}
                />
              </div>
            </div>
          )}
          {hasTableData && (
            <MarksTable
              isCalculating={isCalculating}
              data={currentDocument.extractedData?.table ?? []}
              onDataChange={handleTableChange}
            />
          )}
          <div className="flex justify-between items-center gap-2">
            <div className="flex items-center gap-2">
              <Progress value={progress} className="w-36" />
              <span className="text-xs font-bold p-1 tracking-wide rounded-full">
                {progress}%
              </span>
            </div>

            <div className="flex items-center justify-end gap-2">
              {isDirty && (
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-sky-500"></span>
                  </span>
                  <p className="text-xs text-gray-500">Unsaved changes</p>
                </div>
              )}
              <Button onClick={handleSave} disabled={!isDirty || isCalculating}>
                Save Draft
              </Button>

              {isLastDocument && (
                <Button
                  onClick={() => handleConvert(editedBatch)}
                  disabled={isCalculating}
                >
                  {isCalculating ? "Calculating GPA..." : "Convert Batch"}
                </Button>
              )}
            </div>
          </div>

          <div className="flex justify-between gap-2 items-center">
            <Button
              onClick={handlePrevious}
              variant={"secondary"}
              disabled={isFirstDocument || isCalculating}
              className={`${
                isFirstDocument
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Previous
            </Button>

            {progress < 100 ? (
              <Button
                onClick={handleNext}
                variant={"secondary"}
                disabled={isLastDocument || isCalculating}
                className={`${
                  isLastDocument
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                Next
                <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            ) : batch.documents.length === 1 && allDocumentsComplete ? (
              <div className="font-semibold rounded-full flex items-center gap-2 text-sm">
                Review completed
                <CircleCheck
                  className="size-4 border-[#D8F3E3] text-[#008A1E]"
                  fill="#E8F8F0"
                />
              </div>
            ) : batch.documents.length > 1 && allDocumentsComplete ? (
              <div className="font-semibold rounded-full flex items-center gap-2 text-sm">
                Review completed
                <CircleCheck
                  className="size-4 border-[#D8F3E3] text-[#008A1E]"
                  fill="#E8F8F0"
                />
              </div>
            ) : null}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2">
          <PdfViewer source={currentDocument.fileUrl} />
        </div>
      </div>
    </div>
  );
}

interface Row {
  subject: string;
  credits: string;
  score: string;
}

export function MarksTable({
  data,
  onDataChange,
  isCalculating,
}: {
  data: ExtractedData["table"];
  onDataChange: (data: ExtractedData["table"]) => void;
  isCalculating: boolean;
}) {
  const [rowsToAdd, setRowsToAdd] = useState(1);

  const handleDeleteRow = (index: number) => {
    if (data.length <= 1) {
      toast.warning("Cannot delete the last row");
      return;
    }

    onDataChange(data.filter((_, i) => i !== index));
  };

  const handleInputChange = (
    index: number,
    field: keyof Row,
    value: string
  ) => {
    onDataChange(
      data.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const handleAddRows = () => {
    const newRows = Array.from({ length: rowsToAdd }, () => ({
      subject: "",
      credits: "",
      score: "",
    }));

    onDataChange([...data, ...newRows]);
  };

  return (
    <div>
      <div className="border">
        <Table className="border max-h-[50vh] overflow-y-auto rounded-lg overflow-hidden">
          <TableHeader>
            <TableRow className="divide-x hover:bg-muted/50">
              <TableHead className="text-center">#</TableHead>
              <TableHead>Course</TableHead>
              <TableHead className="w-20">Credits</TableHead>
              <TableHead className="w-22">Grade</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="divide-y divide-border">
            {data.map((row, index) => (
              <TableRow
                key={index}
                className="hover:bg-muted/50 divide-x divide-border"
              >
                <TableCell className="text-center p-0">{index + 1}</TableCell>
                <TableCell>
                  <Input
                    value={row.subject}
                    disabled={isCalculating}
                    onChange={(e) =>
                      handleInputChange(index, "subject", e.target.value)
                    }
                    data-row-index={index}
                    data-field="course"
                    className="border-0 bg-transparent"
                  />
                </TableCell>
                <TableCell className="w-fit">
                  <Input
                    value={row.credits}
                    disabled={isCalculating}
                    onChange={(e) =>
                      handleInputChange(index, "credits", e.target.value)
                    }
                    data-row-index={index}
                    data-field="credits"
                    className="border-0 bg-transparent"
                    type="number"
                  />
                </TableCell>
                <TableCell className="w-fit">
                  <Input
                    value={row.score}
                    disabled={isCalculating}
                    onChange={(e) =>
                      handleInputChange(index, "score", e.target.value)
                    }
                    data-row-index={index}
                    data-field="grade"
                    className="border-0 bg-transparent"
                  />
                </TableCell>
                <TableCell className="text-center p-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={isCalculating}
                    className="size-4 p-0"
                    onClick={() => handleDeleteRow(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter className={cn(data.length === 0 ? "mx-auto" : "hidden")}>
            <TableRow className="text-center p-6">
              <TableCell colSpan={5}>No rows</TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
      <div className="mt-6 flex items-center gap-4">
        <span className="text-sm font-medium">Number of rows to add:</span>
        <div className="flex items-center">
          <Input
            type="number"
            min="1"
            disabled={isCalculating}
            value={rowsToAdd}
            onChange={(e) => setRowsToAdd(Number(e.target.value))}
            className="w-16"
          />
          <Button
            disabled={isCalculating}
            variant="ghost"
            onClick={handleAddRows}
            className="ml-2"
          >
            Add
          </Button>
        </div>
      </div>
    </div>
  );
}
